// Machine SDK — CRUD for shop machines and their hourly rates.
//
// Mirrors src/shared/vendors.ts (which itself mirrors crm.ts). Machines are
// top-level entities at /machines/{machineId}, referenced by Part.machines[]
// (legacy v1 freeform string today; future slice wires the part wizard to
// the typed Machine record).
//
// Why this lives in its own module (not crm.ts):
//   - crm.ts is the original Module 5 entities. Machine is adjacent — same
//     baseline shape and write conventions, different concern (the machine
//     matrix lives in Settings, not the CRM nav). Same rationale as Vendor.
//
// Helper duplication note:
//   The helpers below (sanitizeForWrite, nowIso, makeAuditEntry, newBaseline,
//   readOne, readMany, readModifyWrite, writeSoftDeleteFlag) are private
//   copies of the originals in crm.ts. Per the entity-rollout spec we don't
//   touch crm.ts beyond the one-line CRM_PATHS addition, so we re-paste
//   rather than refactor. If/when a future cleanup pass lifts these into a
//   shared `crmInternals.ts` module, delete this block and import from there.

import {
  ref,
  push,
  set,
  get,
  query,
  orderByChild,
  equalTo,
  type DatabaseReference,
} from 'firebase/database';
import { db, awaitFirebaseUser, currentFirebaseUser } from './firebase';
import { CRM_PATHS } from './crm';
import type {
  Machine,
  BaselineFields,
  AuditEntry,
} from './crmTypes';
import { stripUndefinedDeep, assertFiniteNumbers } from './crmValidators';

// =====================================================
// Private helpers — see "Helper duplication note" above
// =====================================================

function sanitizeForWrite(
  payload: Record<string, unknown>,
  contextLabel: string,
): Record<string, unknown> {
  const stripped = stripUndefinedDeep(payload, contextLabel);
  assertFiniteNumbers(stripped, contextLabel);
  return stripped as Record<string, unknown>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function makeAuditEntry(
  action: AuditEntry['action'],
  extras: Partial<AuditEntry> = {},
): AuditEntry {
  return {
    ts: nowIso(),
    user: currentFirebaseUser()?.uid ?? 'system',
    action,
    source: 'ui',
    ...extras,
  };
}

function appendAudit(
  existing: AuditEntry[] | undefined,
  entry: AuditEntry,
): AuditEntry[] {
  return [...(existing ?? []), entry];
}

function newBaseline(): Omit<BaselineFields, 'id'> {
  const now = nowIso();
  return {
    createdAt: now,
    updatedAt: now,
    _v: 1,
    _audit: [makeAuditEntry('create')],
    _deleted: false,
  };
}

function withUpdateStamp<T extends Record<string, unknown>>(
  updates: T,
): T & { updatedAt: string } {
  return { ...updates, updatedAt: nowIso() };
}

async function readOne<T extends BaselineFields>(
  path: string,
  id: string,
  includeDeleted = false,
): Promise<T | null> {
  const snap = await get(ref(db, `${path}/${id}`));
  if (!snap.exists()) return null;
  const val = snap.val() as Omit<T, 'id'>;
  if (!includeDeleted && (val as { _deleted?: boolean })._deleted) return null;
  return { id, ...val } as T;
}

async function readMany<T extends BaselineFields>(
  path: string,
  opts: {
    orderBy?: string;
    equalToValue?: string | number | boolean;
    includeDeleted?: boolean;
  } = {},
): Promise<T[]> {
  const base = ref(db, path);
  let q;
  if (opts.orderBy && opts.equalToValue !== undefined) {
    q = query(base, orderByChild(opts.orderBy), equalTo(opts.equalToValue));
  } else if (opts.orderBy) {
    q = query(base, orderByChild(opts.orderBy));
  } else {
    q = base;
  }
  const snap = await get(q);
  if (!snap.exists()) return [];
  const out: T[] = [];
  snap.forEach((child) => {
    const val = child.val() as Omit<T, 'id'> & { _deleted?: boolean };
    if (!opts.includeDeleted && val._deleted) return false;
    out.push({ id: child.key!, ...val } as T);
    return false;
  });
  return out;
}

async function readModifyWrite<T extends BaselineFields>(
  path: string,
  id: string,
  entityLabel: string,
  updates: Record<string, unknown>,
): Promise<void> {
  const eRef = ref(db, `${path}/${id}`);
  const snap = await get(eRef);
  if (!snap.exists()) throw new Error(`${entityLabel} ${id} not found`);
  const existing = snap.val() as Omit<T, 'id'>;
  const auditEntry = makeAuditEntry('update', {
    metadata: { changedFields: Object.keys(updates) },
  });
  const merged = {
    ...existing,
    ...withUpdateStamp(updates),
    _audit: appendAudit((existing as { _audit?: AuditEntry[] })._audit, auditEntry),
  };
  await set(
    eRef,
    sanitizeForWrite(merged as Record<string, unknown>, `${entityLabel}.update`),
  );
}

async function writeSoftDeleteFlag<T extends BaselineFields>(
  path: string,
  id: string,
  entityLabel: string,
  deleted: boolean,
  reason?: string,
): Promise<void> {
  const eRef = ref(db, `${path}/${id}`);
  const snap = await get(eRef);
  if (!snap.exists()) throw new Error(`${entityLabel} ${id} not found`);
  const existing = snap.val() as Omit<T, 'id'>;
  const now = nowIso();
  const auditEntry = makeAuditEntry(deleted ? 'soft_delete' : 'restore', {
    metadata: reason ? { reason } : undefined,
  });

  const patch: Record<string, unknown> = {
    _deleted: deleted,
    updatedAt: now,
    _audit: appendAudit((existing as { _audit?: AuditEntry[] })._audit, auditEntry),
  };
  if (deleted) {
    patch._deletedAt = now;
    patch._deletedBy = currentFirebaseUser()?.uid ?? 'system';
    if (reason !== undefined) patch._deletedReason = reason;
  } else {
    patch._deletedAt = null;
    patch._deletedBy = null;
    patch._deletedReason = null;
  }

  const merged = { ...existing, ...patch };
  await set(
    eRef,
    sanitizeForWrite(
      merged as Record<string, unknown>,
      `${entityLabel}.${deleted ? 'softDelete' : 'restore'}`,
    ),
  );
}

// =====================================================
// Machine CRUD
// =====================================================

function machinesRef(): DatabaseReference {
  return ref(db, CRM_PATHS.machines);
}

export type CreateMachineInput = Omit<Machine, keyof BaselineFields>;
export type UpdateMachineInput = Partial<Omit<Machine, keyof BaselineFields | 'id'>>;

export interface ListMachinesFilter {
  /** When true, returns only machines with active !== false. Machines without
   *  the field set are treated as active (back-compat). */
  activeOnly?: boolean;
  includeDeleted?: boolean;
}

export async function createMachine(input: CreateMachineInput): Promise<string> {
  await awaitFirebaseUser();
  // New items land at the bottom of whatever group they're in: compute
  // (max existing sortOrder + 100). If the caller already set sortOrder
  // (rare — only migration tooling), honor that.
  let sortOrder = input.sortOrder;
  if (sortOrder === undefined) {
    const existing = await readMany<Machine>(CRM_PATHS.machines, {});
    const max = existing.reduce(
      (acc, m) => (typeof m.sortOrder === 'number' && m.sortOrder > acc ? m.sortOrder : acc),
      0,
    );
    sortOrder = max + 100;
  }
  const newRef = push(machinesRef());
  const id = newRef.key!;
  const record: Omit<Machine, 'id'> = {
    ...newBaseline(),
    ...input,
    sortOrder,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createMachine'));
  return id;
}

export async function getMachine(id: string): Promise<Machine | null> {
  await awaitFirebaseUser();
  return readOne<Machine>(CRM_PATHS.machines, id);
}

export async function listMachines(filter: ListMachinesFilter = {}): Promise<Machine[]> {
  await awaitFirebaseUser();
  // RTDB only supports one orderByChild per query — order by name and narrow
  // client-side. KMF has a dozen-ish machines tops; client-filter is fine.
  const all = await readMany<Machine>(CRM_PATHS.machines, {
    orderBy: 'name',
    includeDeleted: filter.includeDeleted,
  });
  // Sort by sortOrder ascending (undefined → end), name as tiebreaker.
  // Items without sortOrder yet (legacy data pre-feature) fall to the bottom
  // of every group; Justin can re-order them with the ↑/↓ buttons.
  all.sort((a, b) => {
    const ao = typeof a.sortOrder === 'number' ? a.sortOrder : Number.POSITIVE_INFINITY;
    const bo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
  if (filter.activeOnly) {
    return all.filter((m) => m.active !== false);
  }
  return all;
}

/**
 * Swap the sortOrder of `id` with its neighbor in the requested direction
 * within the same tag group (or the untagged bucket when `tag` is undefined).
 * No-op at boundaries (top going up, bottom going down).
 *
 * Items missing sortOrder are treated as if they sit at the bottom of the
 * group (Number.POSITIVE_INFINITY) for sort purposes; when they need a real
 * number to swap with, we fall back to `(current + 100)` so the write is well-formed.
 */
export async function reorderInGroup(
  id: string,
  direction: 'up' | 'down',
  tag?: string,
): Promise<void> {
  await awaitFirebaseUser();
  const all = await listMachines();
  const inGroup = all.filter((m) => {
    if (tag === undefined) return !m.tags || m.tags.length === 0;
    return (m.tags ?? [])[0] === tag;
  });
  const idx = inGroup.findIndex((m) => m.id === id);
  if (idx === -1) return;
  const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
  if (neighborIdx < 0 || neighborIdx >= inGroup.length) return;
  const self = inGroup[idx];
  const neighbor = inGroup[neighborIdx];
  // Resolve concrete numeric sortOrders for both ends of the swap.
  const selfOrder =
    typeof self.sortOrder === 'number' ? self.sortOrder : (idx + 1) * 100;
  const neighborOrder =
    typeof neighbor.sortOrder === 'number'
      ? neighbor.sortOrder
      : (neighborIdx + 1) * 100;
  await Promise.all([
    updateMachine(self.id, { sortOrder: neighborOrder }),
    updateMachine(neighbor.id, { sortOrder: selfOrder }),
  ]);
}

export async function updateMachine(id: string, updates: UpdateMachineInput): Promise<void> {
  await awaitFirebaseUser();
  await readModifyWrite<Machine>(
    CRM_PATHS.machines,
    id,
    'Machine',
    updates as Record<string, unknown>,
  );
}

export async function softDeleteMachine(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Machine>(CRM_PATHS.machines, id, 'Machine', true, reason);
}

export async function restoreMachine(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Machine>(CRM_PATHS.machines, id, 'Machine', false);
}
